/**
 * Listing Highlight Plugin for Search Results Map Variant
 *
 * Reacts to list-view hover events (delivered via the `listing$` Observable
 * injected at construction time) and shows a mini map card at the listing's
 * geographic position. A transient visual-only marker is placed on the map at
 * the same position so the hovered listing appears highlighted.
 *
 * Depends on `context.services.mapFacade` and `context.root` being present.
 * No inter-plugin service dependencies.
 *
 * Single responsibility: translate list-view hover signals into map card
 * and transient marker lifecycle, delegating all card rendering to the
 * miniMapCard service.
 *
 * This plugin does NOT:
 * - Run on mobile (see carousel.plugin)
 * - Own the card DOM or overlay (see miniMapCard.plugin)
 * - Handle map marker clicks or URL navigation (see markers.plugin)
 * - Know how the hover event originates — it only sees the Observable
 *
 * Reason to change: focused listing resolution logic, transient marker
 * creation, or the bridge between list-view hover and miniMapCard service.
 *
 * @module searchResultsMapVariant/plugins/listingHighlight.plugin
 */

import { filter } from 'rxjs';
import { selectMapResults } from '../redux/selectors';
import { PRICE_PIN_ZOOM_LEVEL } from '../services/markersBehavior';
import { mapMarkerToCardInformation } from '../services/mapper';
import { resolveTooltipDirection } from '../services/cardBuilder';
import {
  createCardOverlay,
  removeCardElements,
  buildCardElement,
  mountCardAtGeoPosition,
} from '../components/miniMapCardDom';
import findDomMarkerElement from '../components/markersDom';
import { isPointInBounds } from '../services/geometry';
import MarkerType from '../enums/markerType';

const FOCUSED_LISTING_TRANSIENT_ID = '__focused_listing_transient';
const FOCUSED_LISTING_Z_INDEX = 1000;

/**
 * Resolves the map result that corresponds to a focused listing.
 * Prefers an exact id match; falls back to communityId.
 *
 * Pure function — no side effects.
 *
 * @param {{ id: string, communityId: string }} focusedListing
 * @param {Object} state - Redux state snapshot.
 * @returns {Object|null} The matching map result, or null if not found.
 */
function resolveFocusedMapResult(focusedListing, state) {
  const results = selectMapResults(state);
  if (!results?.length || !focusedListing) {
    return null;
  }

  if (focusedListing.communityId) {
    const byCommunityId = results.find(({ communityId: resultCommunityId, type }) =>
      String(resultCommunityId) === String(focusedListing.communityId) &&
      [
        MarkerType.HousingDevelopment.toString(),
        MarkerType.BuildablePlan.toString(),
      ].includes(type)
    );
    
    if (byCommunityId) {
      return byCommunityId;
    }
  }

  if (focusedListing.id) {
    return results.find((result) => String(result.id) === String(focusedListing.id));
  }

  return null;
}

/**
 * Creates the listingHighlight plugin.
 *
 * @param {Observable<{ id: string, communityId: string } | null>} listing$
 *   Read-only stream of focused listing changes. Provided by
 *   `services/listingFocus.js` — already deduplicated at the source.
 * @returns {{ name: string, init: Function, start: Function, stop: Function }}
 */
export default function createListingHighlightPlugin(listing$) {
  const pluginState = {
    started: false,
    mapFacade: null,
    mapContainer: null,
    cardOverlay: null,
    isDesktopBreakpoint: null,
    getState: null,
    transientFocusedListingMarker: null,
    hoveredMarkerElement: null,
    subscriptions: [],
  };

  function removeCard() {
    removeCardElements(pluginState.cardOverlay);
  }

  function clearTransientMarker() {
    if (pluginState.transientFocusedListingMarker) {
      pluginState.transientFocusedListingMarker.map = null;
      pluginState.transientFocusedListingMarker = null;
    }

    if (pluginState.hoveredMarkerElement) {
      pluginState.hoveredMarkerElement.classList.remove('nhs-c-marker--hovered');
      pluginState.hoveredMarkerElement = null;
    }
  }

  /**
   * Reacts to a focused listing change from the `listing$` stream.
   *
   * Clears any active card and transient marker, then — if a valid listing is
   * present — creates a new transient marker and mounts a card at the
   * listing's geographic position.
   *
   * @param {{ id: string, communityId: string } | null} focusedListing
   */
  function handleFocusedListingChange(focusedListing) {
    removeCard();
    clearTransientMarker();

    if (!focusedListing?.id && !focusedListing?.communityId) {
      return;
    }

    const mapResult = resolveFocusedMapResult(focusedListing, pluginState.getState());
    if (!mapResult) {
      return;
    }

    const isPointInBoundsResult = isPointInBounds(mapResult.position, pluginState.mapFacade?.getBounds?.());
    if (!isPointInBoundsResult) {
      return;
    }

    const snapshot = pluginState.mapFacade?.getCameraSnapshot?.() ?? null;
    const zoom = snapshot?.zoom ?? 0;

    if (zoom >= PRICE_PIN_ZOOM_LEVEL) {
      // At price-pin zoom the managed marker is already rendered in the DOM.
      // Prefer applying hover state directly to it; never create on demand.
      const domMarker = findDomMarkerElement(mapResult, pluginState.mapContainer);
      if (!domMarker) {
        return;
      }
      
      domMarker.classList.add('nhs-c-marker--hovered');
      pluginState.hoveredMarkerElement = domMarker;
    } else {
      // createUnmanagedMarker bypasses the pool and spatial index so this
      // transient marker never interferes with visibility updates for the
      // surrounding managed markers.
      pluginState.transientFocusedListingMarker = pluginState.mapFacade?.createUnmanagedMarker?.({
        id: FOCUSED_LISTING_TRANSIENT_ID,
        position: mapResult.position,
        zIndex: FOCUSED_LISTING_Z_INDEX,
        title: mapResult.title ?? '',
        type: mapResult.type,
        communityId: mapResult.communityId,
        isBasic: mapResult.isBasic ?? false,
        isAdult: mapResult.isAdult ?? false,
        isMasterPlannedCommunity: mapResult.isMasterPlannedCommunity ?? false,
      });
      // Apply the hover visual state immediately — the focused listing marker
      // should appear highlighted without requiring a real pointer hover.
      pluginState.transientFocusedListingMarker?.content?.classList?.add('nhs-c-marker--hovered');
    }

    if (!pluginState.mapContainer || !pluginState.cardOverlay) {
      return;
    }

    const cardData = { ...mapMarkerToCardInformation(mapResult), type: mapResult.type };
    const cardElement = buildCardElement(cardData, () => {
      clearTransientMarker();
      removeCard();
    });
    if (!cardElement) {
      return;
    }

    const bounds = pluginState.mapFacade?.getBounds?.();
    if (!bounds) {
      return;
    }

    const direction = resolveTooltipDirection(bounds, mapResult.position);
    mountCardAtGeoPosition(
      cardElement,
      mapResult.position,
      bounds,
      pluginState.mapContainer,
      pluginState.cardOverlay,
      direction
    );
  }

  function setupSubscriptions() {
    pluginState.subscriptions.push(
      listing$.pipe(
        filter(() => pluginState.isDesktopBreakpoint?.() ?? false),
      ).subscribe(handleFocusedListingChange),
    );
  }

  function clearSubscriptions() {
    pluginState.subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
    pluginState.subscriptions = [];
  }

  return {
    name: 'listing-highlight',

    async init(context) {
      context.state.listingHighlight = { started: false };
    },

    async start(context) {
      if (pluginState.started) {
        return;
      }

      pluginState.mapFacade = context.services.mapFacade;
      pluginState.mapContainer = context.root;
      pluginState.isDesktopBreakpoint = context.services.isDesktopBreakpoint;
      pluginState.getState = context.services.getState;

      if (!pluginState.mapContainer) {
        return;
      }

      pluginState.cardOverlay = createCardOverlay(pluginState.mapContainer);

      setupSubscriptions();

      pluginState.started = true;
      context.state.listingHighlight = { started: true };
    },

    async stop(context) {
      clearSubscriptions();
      removeCard();
      clearTransientMarker();

      pluginState.cardOverlay?.remove();
      pluginState.cardOverlay = null;

      pluginState.mapFacade = null;
      pluginState.mapContainer = null;
      pluginState.isDesktopBreakpoint = null;
      pluginState.getState = null;
      pluginState.started = false;

      delete context.state.listingHighlight;
    },
  };
}
